import {
  AccountVerifiedAlert,
  ActivationRequiredAlert,
  EmailNotFoundAlert,
  IncorrectEmailPasswordAlert,
  PasswordExpiredAlert,
  LinkExpiredAlert,
  PasswordResetSuccessfulAlert,
  UnexpectedErrorAlert,
} from '@/components/alerts';
import { EMAIL_REGEX, EMAIL_VALIDATION_MESSAGE } from '@/constants';
import { SubmitButton } from '@/components/SubmitButton';
import { AccountCenterBox } from '@/layout/AccountCenterBox';
import { CustomInput } from '@/components/CustomInput';
import { PasswordInput } from '@/components/PasswordInput';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { api } from '@/services/api';
import { LoginResponse, LoginResultEnum } from '@shared/types';
import { AxiosResponse } from 'axios';
import { Field, Form, Formik } from 'formik';
import { Link, useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import { TurnstileField } from '@/components/TurnstileField';
import { useTurnstile } from '@/hooks/useTurnstile';

enum FormStatus {
  NoAlert = 'NoAlert',
  AccountVerified = 'AccountVerified',
  ActivationRequiredAlert = 'ActivationRequiredAlert',
  ActivationLinkExpired = 'ActivationLinkExpired',
  EmailNotFoundAlert = 'EmailNotFoundAlert',
  IncorrectEmailPasswordAlert = 'IncorrectEmailPasswordAlert',
  PasswordExpiredAlert = 'PasswordExpiredAlert',
  UnexpectedError = 'UnexpectedErrorAlert',
  PasswordResetSuccessfulAlert = 'PasswordResetSuccessfulAlert',
}

export const Login = () => {
  useDocumentTitle('Marshall Reddick - Login');
  const location = useLocation();
  const { getToken, reset, widgetProps, widgetError } = useTurnstile();
  const initialStatus =
    location.state?.status === 'AccountVerified'
      ? FormStatus.AccountVerified
      : location.state?.status === 'PasswordResetSuccessful'
        ? FormStatus.PasswordResetSuccessfulAlert
        : location.state?.status === 'ActivationLinkExpired'
          ? FormStatus.ActivationLinkExpired
          : FormStatus.NoAlert;

  const alertMap: Partial<Record<FormStatus, React.ReactNode>> = {
    [FormStatus.AccountVerified]: <AccountVerifiedAlert />,
    [FormStatus.ActivationLinkExpired]: <LinkExpiredAlert />,
    [FormStatus.PasswordResetSuccessfulAlert]: <PasswordResetSuccessfulAlert />,
    [FormStatus.IncorrectEmailPasswordAlert]: <IncorrectEmailPasswordAlert />,
    [FormStatus.ActivationRequiredAlert]: <ActivationRequiredAlert />,
    [FormStatus.EmailNotFoundAlert]: <EmailNotFoundAlert />,
    [FormStatus.PasswordExpiredAlert]: <PasswordExpiredAlert />,
    [FormStatus.UnexpectedError]: <UnexpectedErrorAlert />,
  };

  return (
    <Formik
      initialValues={{ email: '', password: '', turnstileToken: '' }}
      initialStatus={initialStatus}
      validationSchema={Yup.object().shape({
        email: Yup.string()
          .trim()
          .matches(EMAIL_REGEX, EMAIL_VALIDATION_MESSAGE)
          .transform((value) => value.trim()),
        password: Yup.string(),
      })}
      validateOnMount={false}
      onSubmit={async (values, { setFieldError, setStatus, setFieldValue }) => {
        if (!values.email.trim()) {
          setFieldError('email', 'Email Address must be a valid email');
        }
        if (values.password.length < 8) {
          setFieldError('password', 'Password must be at least 8 characters');
        }
        if (!values.email.trim() || !values.password.trim()) {
          return;
        }

        const turnstileToken = await getToken();

        if (!turnstileToken) {
          setFieldValue('turnstileToken', '');
          setFieldError('turnstileToken', 'Security check failed, please try again');
          reset();
          return;
        }

        setStatus(FormStatus.NoAlert);

        try {
          const response: AxiosResponse<LoginResponse> = await api.post('/auth/login', {
            email: values.email,
            password: values.password,
            turnstileToken,
          });

          const { result } = response.data;
          if (result === LoginResultEnum.SUCCESS) {
            setStatus(FormStatus.NoAlert);
            const websiteUrl = import.meta.env.VITE_WEBSITE_URL;
            window.location.replace(websiteUrl);
          } else if (result === LoginResultEnum.INVALID_CREDENTIALS) {
            setStatus(FormStatus.IncorrectEmailPasswordAlert);
          } else if (result === LoginResultEnum.USER_NOT_FOUND) {
            setStatus(FormStatus.EmailNotFoundAlert);
          } else if (result === LoginResultEnum.UNVERIFIED) {
            setStatus(FormStatus.ActivationRequiredAlert);
          } else if (result === LoginResultEnum.FORCED_PASSWORD_RESET) {
            setStatus(FormStatus.PasswordExpiredAlert);
          } else {
            setStatus(FormStatus.UnexpectedError);
          }
        } catch {
          setStatus(FormStatus.UnexpectedError);
        } finally {
          reset();
        }
      }}
    >
      {({ errors, touched, isSubmitting, status }) => {
        const statusKey = status as FormStatus;
        return (
          <AccountCenterBox isSubmitting={isSubmitting}>
            <Form noValidate>
              {statusKey in alertMap ? alertMap[statusKey] : null}
              {/* EMAIL */}
              <Field
                name="email"
                as={CustomInput}
                type="email"
                label="Email Address"
                error={errors.email}
                touched={touched.email}
              />
              {/* PASSWORD */}
              <Field
                name="password"
                as={PasswordInput}
                error={errors.password}
                touched={touched.password}
              />
              {/* TURNSTILE */}
              <TurnstileField
                widgetProps={widgetProps}
                widgetError={widgetError}
                error={errors.turnstileToken}
              />
              {/* BUTTON */}
              <SubmitButton
                text="Log In"
                className="mt-6"
                isSubmitting={isSubmitting}
                disabled={false}
              />
              {/* LINKS */}
              <div className="flex justify-between text-sm pt-1 mt-2">
                <Link to="/forgot-password" className="text-link">
                  Forgot Password?
                </Link>
                <Link to="/register" className="text-link">
                  Create Account
                </Link>
              </div>
            </Form>
          </AccountCenterBox>
        );
      }}
    </Formik>
  );
};
